/**
 * MapToolbox — registry-driven map tool surface.
 *
 * Renders as a row of chips under the search bar, placed by `MapTopBar`,
 * styled to match it (rounding, shadow) — same pill chrome as the
 * "Import CSV" / "Sync from Notion" actions elsewhere in the app. In compact
 * mode the row folds into one Tools button that opens the chips in a popover.
 *
 * Each button carries its name via `aria-label` and — when gated — a Radix
 * tooltip explaining why. Workspace-gated tools are `aria-disabled` (kept
 * focusable) with the actionable reason in the enriched `aria-label`. Arming a
 * tool moves focus into its panel, which opens under the pills, 20px from the
 * right; disarming returns focus to the trigger (skipped if that trigger is
 * now gated — see the focus-management effect below).
 */
import { Wrench } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buttonVariants } from '@/components/atoms/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { cn } from '@/libs/utils/cn';
import {
  MAP_LAYER_PANEL_WIDTH_CLASS,
  MAP_PILL_CLASS,
  MAP_TOOLBOX_PANEL_CLASS,
} from './mapBarGeometry';
import { MAP_PANEL_SURFACE } from './mapPanelSurface';
import { COMPUTING_HINT, type MapTool, WORKSPACE_REQUIRED_HINT } from './tools/mapTool.types';
import { MAP_TOOLS } from './tools/mapToolRegistry';

interface MapToolboxProps {
  /** Shell state from MapShell's render-prop (live map ref, load flags). */
  shell: MapShellRenderState;
  /** The active workspace id from `?workspace=<id>`, or null when none. */
  activeWorkspaceId: string | null;
  /** Whether a compute run is currently active — gates `blockedWhileComputing` tools. */
  isComputing?: boolean;
  /** Currently armed tool id — controlled by the parent. */
  armedToolId: string | null;
  /** Called when the user arms or disarms a tool. Parent owns the state. */
  onArmChange: (id: string | null) => void;
  /**
   * Tool descriptors to render. Defaults to MAP_TOOLS from the registry.
   * Injectable for tests so the suite is hermetic from the real registry.
   */
  tools?: MapTool[];
  /** Disables every tool (no workspace selected yet). */
  disabled?: boolean;
  /** Folds the row into one Tools button. */
  compact?: boolean;
}

export function MapToolbox({
  shell,
  activeWorkspaceId,
  isComputing = false,
  armedToolId,
  onArmChange,
  tools = MAP_TOOLS,
  disabled = false,
  compact = false,
}: MapToolboxProps) {
  const [toolsOpen, setToolsOpen] = useState(false);
  // Focus management refs: the panel (focused on arm) and the trigger buttons
  // (one is re-focused on disarm). `prevArmedRef` remembers which tool was armed
  // so we know which trigger to restore focus to.
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const prevArmedRef = useRef<string | null>(null);

  // One ref-callback per tool id, reused across renders: a fresh inline ref
  // defeats Radix Tooltip's ref memoization and can spiral into
  // "Maximum update depth exceeded".
  const triggerRefCallbacks = useRef<Map<string, (el: HTMLButtonElement | null) => void>>(
    new Map(),
  );
  const getTriggerRefCallback = (toolId: string) => {
    let callback = triggerRefCallbacks.current.get(toolId);
    if (!callback) {
      callback = (el) => {
        if (el) triggerRefs.current.set(toolId, el);
        else triggerRefs.current.delete(toolId);
      };
      triggerRefCallbacks.current.set(toolId, callback);
    }
    return callback;
  };

  /** Disarm the currently armed tool (if any). */
  const disarm = useCallback(() => onArmChange(null), [onArmChange]);

  /** Toggle a tool: arm it if not armed, disarm if already armed. */
  const toggleTool = (toolId: string) => {
    onArmChange(armedToolId === toolId ? null : toolId);
    setToolsOpen(false);
  };

  // Escape key disarms the currently armed tool.
  useEffect(() => {
    if (armedToolId === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        disarm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [armedToolId, disarm]);

  // Focus management: arm → move focus into the panel; disarm → return focus to
  // the trigger that was armed. Initial mount (null → null) steals no focus.
  // Skipped when that trigger is now gated (e.g. a manual-pin launch disarms
  // the tool right as a compute run starts): Radix Tooltip opens on focus as
  // well as hover, so programmatically focusing a gated trigger would pop its
  // "why disabled" tooltip open on its own — it should only show on an actual
  // mouse hover.
  useEffect(() => {
    if (armedToolId !== null) {
      panelRef.current?.focus();
    } else if (prevArmedRef.current !== null) {
      const prevTool = tools.find((t) => t.id === prevArmedRef.current);
      const isPrevToolGated =
        prevTool != null &&
        ((prevTool.requiresWorkspace && activeWorkspaceId === null) ||
          (Boolean(prevTool.blockedWhileComputing) && isComputing));
      if (!isPrevToolGated) {
        triggerRefs.current.get(prevArmedRef.current)?.focus();
      }
    }
    prevArmedRef.current = armedToolId;
  }, [armedToolId, tools, activeWorkspaceId, isComputing]);

  const armedTool = tools.find((t) => t.id === armedToolId) ?? null;

  const renderButton = (tool: MapTool) => {
    const isWorkspaceGated = disabled || (tool.requiresWorkspace && activeWorkspaceId === null);
    const isComputeGated = Boolean(tool.blockedWhileComputing) && isComputing;
    const isGated = isWorkspaceGated || isComputeGated;
    const gatedHint = isWorkspaceGated ? WORKSPACE_REQUIRED_HINT : COMPUTING_HINT;
    const gatedLabelSuffix = isWorkspaceGated
      ? 'select or create a workspace first'
      : 'wait for the current location to finish computing';
    const isArmed = armedToolId === tool.id;
    const Icon = tool.icon;

    const button = (
      <button
        type="button"
        ref={getTriggerRefCallback(tool.id)}
        aria-label={isGated ? `${tool.label} — ${gatedLabelSuffix}` : tool.label}
        aria-pressed={isGated ? undefined : isArmed}
        aria-disabled={isGated ? 'true' : undefined}
        onClick={() => {
          if (!isGated) {
            toggleTool(tool.id);
          }
        }}
        className={cn(
          buttonVariants({ size: 'sm' }),
          // Matches the search bar's corner radius and shadow — overrides the
          // default variant's CTA border/shadow/color, kept only for its
          // structural (sizing, focus-ring) classes.
          'h-[34px] rounded-[10px] border-transparent shadow-modal',
          'bg-white text-neutral-600 hover:bg-neutral-100',
          isArmed && 'bg-neutral-100 hover:bg-neutral-100',
          isGated && 'cursor-not-allowed opacity-40',
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
        {tool.label}
      </button>
    );

    // The visible label already names the tool — a hover tooltip is only
    // worth showing when there's something extra to say: why a gated tool
    // is disabled.
    if (!isGated) {
      return <div key={tool.id}>{button}</div>;
    }

    return (
      <Tooltip key={tool.id}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        {/* Tooltip opens to the left (inward, away from the screen edge). */}
        <TooltipContent side="left">{gatedHint}</TooltipContent>
      </Tooltip>
    );
  };

  if (tools.length === 0) return null;

  // Focusable (tabIndex -1) so focus management can land here on arm.
  // Card treatment matches MapWorkspaceSelectHint's helper-callout style.
  const panel = armedTool !== null && (
    <div
      ref={panelRef}
      tabIndex={-1}
      data-testid="map-tool-panel"
      className={cn('rounded-md outline-none', MAP_PANEL_SURFACE)}
    >
      {armedTool.renderPanel({ shell, close: disarm })}
    </div>
  );

  // Positioned, so the row paints above the search bar's shadow, which reaches past the gap. As
  // wide as the cards under it, the tools at its two edges.
  const row = (
    <div
      role="group"
      aria-label="Map tools"
      className={cn(MAP_LAYER_PANEL_WIDTH_CLASS, 'relative flex items-center justify-between')}
    >
      {tools.map(renderButton)}
    </div>
  );

  // The panel sits under the pills on the right, so it leaves the bar for the chrome.
  const chrome = shell.containerRef.current;
  const placedPanel = panel != null && <div className={MAP_TOOLBOX_PANEL_CLASS}>{panel}</div>;

  return (
    <>
      {compact ? (
        <Popover open={toolsOpen} onOpenChange={setToolsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-pressed={armedToolId !== null}
              className={cn(
                MAP_PILL_CLASS,
                'px-3 font-medium text-neutral-600',
                armedToolId !== null && 'bg-neutral-100',
              )}
            >
              <Wrench className="size-5" aria-hidden="true" />
              Tools
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-auto rounded-[10px] border-border p-2 shadow-modal"
          >
            <div role="group" aria-label="Map tools" className="flex flex-col gap-1.5">
              {tools.map(renderButton)}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        row
      )}
      {chrome ? createPortal(placedPanel, chrome) : placedPanel}
    </>
  );
}
